import React from 'react';
import {
  IonItem,
  IonLabel,
  IonDatetimeButton,
  IonModal,
  IonDatetime,
} from '@ionic/react';
import SectionCard from '@/components/SectionCard';
import type { TimezoneOption } from '../timezone';
import { visuallyHiddenInputStyle } from './constants';
import LocationRow from './LocationRow';
import TimezoneSelect from './TimezoneSelect';
import { useZoneFollowingLocation } from './useZoneFollowingLocation';

interface EditableWhereWhenBlockProps {
  startTime: string;
  setStartTime: (value: string) => void;
  endTime: string;
  setEndTime: (value: string) => void;
  timezone: string;
  setTimezone: (value: string) => void;
  timezones: readonly TimezoneOption[];
  endBeforeStart: boolean;
  /**
   * The topic's location, for a type that has one. With `setLocation` the
   * card is "Where & when", with the location above the start, end and time
   * zone, and the time zone follows the location as in
   * `useZoneFollowingLocation`; without it the card is "When".
   */
  location?: string;
  setLocation?: (value: string) => void;
  /** The location row's label, in sentence case; "Location" unless given. */
  locationLabel?: string;
}

const ignoreLocation = () => {};

/**
 * The create/edit form's "Where & when" card: the topic's location, for a
 * type that has one, above its start/end datetime and time zone controls.
 */
const EditableWhereWhenBlock: React.FC<EditableWhereWhenBlockProps> = ({
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  timezone,
  setTimezone,
  timezones,
  endBeforeStart,
  location = '',
  setLocation,
  locationLabel = 'Location',
}) => {
  const { changeLocation, pickZone } = useZoneFollowingLocation(
    location,
    setLocation ?? ignoreLocation,
    setTimezone
  );
  const clampEndToStart = () => {
    if (endBeforeStart) setEndTime(startTime);
  };

  return (
    <>
      <SectionCard title={setLocation ? 'Where & when' : 'When'}>
        {setLocation && (
          <LocationRow
            location={location}
            onChange={changeLocation}
            onClear={() => setLocation('')}
            label={locationLabel}
            lines="inset"
          />
        )}
        <IonItem lines="inset">
          <IonLabel>Starts</IonLabel>
          <IonDatetimeButton
            datetime="start-datetime"
            slot="end"
            data-testid="start-datetime-button"
          />
        </IonItem>
        <IonItem lines="inset">
          <IonLabel>Ends</IonLabel>
          <IonDatetimeButton
            datetime="end-datetime"
            slot="end"
            data-testid="end-datetime-button"
            onClick={clampEndToStart}
          />
        </IonItem>
        <TimezoneSelect
          timezone={timezone}
          setTimezone={pickZone}
          timezones={timezones}
        />
      </SectionCard>

      <IonModal keepContentsMounted={true}>
        <IonDatetime
          id="start-datetime"
          presentation="date-time"
          value={startTime}
          onIonChange={e => {
            const v = e.detail.value;
            if (typeof v === 'string') setStartTime(v);
          }}
        />
      </IonModal>
      <IonModal keepContentsMounted={true}>
        <IonDatetime
          id="end-datetime"
          presentation="date-time"
          value={endTime}
          onIonChange={e => {
            const v = e.detail.value;
            if (typeof v === 'string') setEndTime(v);
          }}
        />
      </IonModal>

      <input
        type="datetime-local"
        data-testid="start-datetime-input"
        value={startTime.slice(0, 16)}
        onChange={e => setStartTime(`${e.target.value}:00`)}
        style={visuallyHiddenInputStyle}
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        type="datetime-local"
        data-testid="end-datetime-input"
        value={endTime.slice(0, 16)}
        onFocus={clampEndToStart}
        onChange={e => setEndTime(`${e.target.value}:00`)}
        style={visuallyHiddenInputStyle}
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        type="text"
        data-testid="timezone-input"
        value={timezone}
        onChange={e => setTimezone(e.target.value)}
        style={visuallyHiddenInputStyle}
        aria-hidden="true"
        tabIndex={-1}
      />
    </>
  );
};

export default EditableWhereWhenBlock;
